import { useContext } from "@nuxtjs/composition-api";
import { useDataTableFilterOperators } from "../data-table/useDataTableFilterOperators";
import { useDataTableQuery } from "../data-table/useDataTableQuery";
import { useRatingActions } from "../ratings/useRatingActions";
import { useTenderEnums } from "../tenders/useTenderEnums";

export const REFUND_PAYMENT_METHODS = {
  BANK_TRANSFER: "BANK_TRANSFER",
  CREDIT_CARD: "CREDIT_CARD",
};

const REFUNDABLE_PAYMENT_TYPES = ["bankcard", "stored_bankcard"];

export const isRefundablePayment = (payment) =>
  !!payment &&
  payment.status === "paid" &&
  REFUNDABLE_PAYMENT_TYPES.includes(payment.type) &&
  !payment.isCardRegistration;

export function usePersonActions() {
  const { $mutate, $query } = useContext();
  const operators = useDataTableFilterOperators();

  const ratingsActions = useRatingActions();
  const { TEXT_CHANNEL_STATUS } = useTenderEnums();

  async function deletePersonById(id) {
    try {
      const DELETE_PERSON = await import("./deletePersonById.graphql");
      await $mutate(DELETE_PERSON, { id });
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function getPersonById(id) {
    try {
      const GET_PERSON = await import("./getPersonById.graphql");
      const { personById } = await $query(GET_PERSON, { id });
      return personById;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function getPersonPersonTenderInfo({
    personId,
    professionIds,
    startDate,
    endDate,
  }) {
    try {
      const GET_PERSON_TENDER_INFO = await import(
        "./getPersonTenderInfo.graphql"
      );
      const { personById } = await $query(GET_PERSON_TENDER_INFO, {
        personId,
        professionIds,
        startDate,
        endDate,
      });
      return personById;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  const getPersons = useDataTableQuery(() =>
    import("./personsDataTable.graphql")
  );

  async function getPersonTenders({
    page,
    pageSize,
    startDate,
    endDate,
    professionIds,
    personId,
  }) {
    try {
      const GET_PERSON_TENDERS = await import("./getPersonTenders.graphql");

      const params = {
        page,
        pageSize,
        personId,
        order: "desc",
        orderBy: "created",
        filters: [
          {
            filterId: "receivedByPerson",
            operator: operators.EQUALS,
            value: personId,
          },
          {
            filterId: "textChannelStatus",
            operator: operators.IN,
            items: [
              TEXT_CHANNEL_STATUS.APPROVED_TENDER,
              TEXT_CHANNEL_STATUS.NO_TEXT_CHANNEL,
            ],
          },
        ],
      };

      if (startDate && endDate) {
        params.filters.push({
          filterId: "created",
          operator: operators.BETWEEN,
          min: startDate,
          max: endDate,
        });
      }
      if (professionIds?.length) {
        params.filters.push({
          filterId: "professionId",
          operator: operators.IN,
          items: professionIds,
        });
      }

      const { tendersDataTable } = await $query(GET_PERSON_TENDERS, params);
      return tendersDataTable;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function getRequestedTenders({ page, pageSize, personId }) {
    try {
      const GET_REQUESTED_TENDERS = await import(
        "./requested-tenders/getRequestedTenders.graphql"
      );

      const params = {
        page,
        pageSize,
        order: "desc",
        orderBy: "created",
        filters: [
          {
            filterId: "byPerson",
            operator: operators.EQUALS,
            value: personId,
          },
        ],
      };

      const { tendersDataTable } = await $query(GET_REQUESTED_TENDERS, params);
      return tendersDataTable;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function getPersonMarketTenders({
    page,
    pageSize,
    personId,
    statuses,
    rounds,
    startDate,
    endDate,
  }) {
    try {
      const GET_PERSON_MARKET_TENDERS = await import(
        "./market-tenders/getPersonMarketTenders.graphql"
      );

      const params = {
        page,
        pageSize,
        personId,
        order: "desc",
        orderBy: "created",
        filters: [
          {
            filterId: "personId",
            operator: operators.EQUALS,
            value: personId,
          },
        ],
      };

      if (statuses?.length) {
        params.filters.push({
          filterId: "status",
          operator: operators.IN,
          items: statuses,
        });
      }
      if (rounds?.length) {
        params.filters.push({
          filterId: "round",
          operator: operators.IN,
          items: rounds,
        });
      }
      if (startDate && endDate) {
        params.filters.push({
          filterId: "created",
          operator: operators.BETWEEN,
          min: startDate,
          max: endDate,
        });
      }

      const { personMarketTendersDataTable } = await $query(
        GET_PERSON_MARKET_TENDERS,
        params
      );
      return personMarketTendersDataTable;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function getPersonRatings({ page, pageSize, personId }) {
    try {
      const params = {
        page,
        pageSize,
        order: "desc",
        orderBy: "created",
        filters: [
          {
            filterId: "mateId",
            operator: operators.EQUALS,
            value: personId,
          },
        ],
      };

      return await ratingsActions.getRatings(params);
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function requestRefundForPerson({
    professionalId,
    amount,
    refundPaymentMethod,
    accountNumber,
    accountHolder,
    paymentId,
  }) {
    try {
      const REQUEST_REFUND = await import("./requestRefund.graphql");
      const { requestRefund } = await $mutate(
        REQUEST_REFUND,
        {
          input: {
            professionalId,
            amount,
            refundPaymentMethod,
            bankTransferDetails:
              refundPaymentMethod === REFUND_PAYMENT_METHODS.BANK_TRANSFER
                ? {
                    accountNumber,
                    accountHolder,
                  }
                : undefined,
            paymentId:
              refundPaymentMethod === REFUND_PAYMENT_METHODS.CREDIT_CARD
                ? paymentId
                : undefined,
          },
        },
        {
          showSuccessToast: true,
          showErrorToast: true,
        }
      );
      return requestRefund;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function toggleProfessionNotification({ personId, professionId }) {
    try {
      const TOGGLE_PROFESSION_NOTIFICATION = await import(
        "./toggleProfessionNotification.graphql"
      );
      const { toggleProfessionNotification } = await $mutate(
        TOGGLE_PROFESSION_NOTIFICATION,
        {
          personId,
          professionId,
        },
        {
          showSuccessToast: true,
          showErrorToast: true,
        }
      );
      return toggleProfessionNotification;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function anonymizePerson({ id }) {
    try {
      const ANONYMIZE_PERSON = await import("./anonymizePerson.graphql");
      const { anonymizePerson } = await $mutate(
        ANONYMIZE_PERSON,
        {
          id,
        },
        {
          showSuccessToast: true,
          showErrorToast: true,
        }
      );
      return anonymizePerson;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  return {
    deletePersonById,
    getPersonById,
    getPersonPersonTenderInfo,
    getPersons,
    getPersonTenders,
    getRequestedTenders,
    getPersonMarketTenders,
    getPersonRatings,
    requestRefundForPerson,
    toggleProfessionNotification,
    anonymizePerson,
  };
}
